import { Component, EventListener } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import {
	setLocalStorageItem,
	localStorageValueExists,
	deleteLocalStorageItem,
} from '@swisslife/shared-ui/src/utils/local-storage/js/local-storage';
import { getCurrentViewport, VIEWPORT_ENTERED } from '@gondel/plugin-media-queries';
import { WINDOW_RESIZED } from '@swisslife/shared-ui/src/utils/gondel-resize/js/gondel-resize';
import shave from 'shave';

enum States {
	IsFavorite = 'state-m-teaser--is-favorite',
	PreInit = 'state-m-teaser--pre-init',
}

enum Selectors {
	FavoriteButton = '.js-m-teaser__head-favorite-button',
	TeaserTitle = '.js-m-teaser__content-title',
	TeaserText = '.js-m-teaser__content-text',
}

interface ITeaserConfiguration {
	teaserId: string;
	localStorageName: string;
}

interface ITextTruncation {
	$wrapper: JQuery;
	height: {
		small: number;
		large: number;
	};
	selector: string;
}

@Component('Teaser')
export class Teaser extends GondelJqueryComponent {
	private truncationElements: ITextTruncation[] = [];
	private uxBehaviourType;
	private isFavorite: boolean = false;
	private configuration: ITeaserConfiguration;

	public start() {
		const currentVp = getCurrentViewport();
		this.updateUxBehaviourType(currentVp);
		this.parseTextTruncationConfig();
		this.configuration = {
			teaserId: this.$ctx.data('teaser-id'),
			localStorageName: this.$ctx.data('local-storage-name'),
		};
	}

	public sync() {
		this.initializeTextTruncation(this.uxBehaviourType);
		this.initializeFavoriteState();
	}

	public initializeTextTruncation(heightType: string) {
		// state needs to get removed BEFORE init of shave to allow proper measurement
		this.$ctx.removeClass(States.PreInit);

		for (const truncationElement of this.truncationElements) {
			const $element = $(truncationElement.selector, truncationElement.$wrapper);

			if ($element.length > 0) {
				shave($element.get(0), truncationElement.height[heightType], { classname: 'js-m-teaser__shave' });
			}
		}
	}

	@EventListener(VIEWPORT_ENTERED)
	public _handleViewportChangeEvent(event) {
		const previousUxBehaviourType = this.uxBehaviourType;
		this.updateUxBehaviourType(event.viewport);

		if (previousUxBehaviourType !== this.uxBehaviourType) {
			this.initializeTextTruncation(this.uxBehaviourType);
		}
	}

	@EventListener(WINDOW_RESIZED)
	public _handleWindowResizeEvent() {
		this.initializeTextTruncation(this.uxBehaviourType);
	}

	@EventListener('click', Selectors.FavoriteButton)
	public _handleFavoriteClick() {
		if (this.isFavorite) {
			deleteLocalStorageItem(this.configuration.localStorageName, this.configuration.teaserId);
		} else {
			setLocalStorageItem(this.configuration.localStorageName, this.configuration.teaserId);
		}
		this.isFavorite = !this.isFavorite;
		this.toggleIsFavoriteStateClass();
		this.toggleIsFavoriteAttribute();
	}

	private initializeFavoriteState() {
		this.isFavorite = localStorageValueExists(this.configuration.localStorageName, this.configuration.teaserId);
		this.toggleIsFavoriteStateClass();
		this.toggleIsFavoriteAttribute();
	}

	private toggleIsFavoriteStateClass() {
		this.$ctx.toggleClass(States.IsFavorite, this.isFavorite);
	}

	private toggleIsFavoriteAttribute() {
		// the data-favorized attribute is used by the AEM backend to track the favorized-state for analytics purposes.
		this.$ctx.attr('data-favorized', `${this.isFavorite}`);
	}

	private updateUxBehaviourType(viewport: string) {
		if (viewport === 'medium' || viewport === 'large' || viewport === 'xlarge' || viewport === 'xxlarge') {
			this.uxBehaviourType = 'large';
		} else {
			this.uxBehaviourType = 'small';
		}
	}

	private parseTextTruncationConfig() {
		const $teaserTitle = $(Selectors.TeaserTitle, this.$ctx);
		const $teaserText = $(Selectors.TeaserText, this.$ctx);

		if ($teaserTitle.length > 0) {
			this.truncationElements.push({
				$wrapper: $teaserTitle,
				height: {
					small: $teaserTitle.data('height-small'),
					large: $teaserTitle.data('height-large'),
				},
				selector: $teaserTitle.data('selector'),
			});
		}

		if ($teaserText.length > 0) {
			this.truncationElements.push({
				$wrapper: $teaserText,
				height: {
					small: $teaserText.data('height-small'),
					large: $teaserText.data('height-large'),
				},
				selector: $teaserText.data('selector'),
			});
		}
	}
}
